/**
 * Trang rut tien.
 *
 * Quy tac chi phoi toan bo man hinh nay (docs/22 muc 9): giao dien PHAI hien
 * moi ly do chan rut TRUOC khi nguoi choi bam gui. De ho nhap so, bam gui, roi
 * moi bao loi la nguyen nhan khieu nai hang dau — va bao tung loi mot, bat ho
 * sua roi thu lai, con te hon.
 *
 * Vi vay man hinh goi diem cuoi kiem tra dieu kien lien tuc va hien TOAN BO
 * vuong mac, kem huong dan khac phuc cho tung loai.
 */

import { useEffect, useMemo, useState, type JSX } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  api,
  ApiError,
  newIdempotencyKey,
  type PayoutAccount,
  type RejectionReason,
  type WithdrawalCreated,
} from '../lib/api';
import { formatMoney, parseAmount } from '../lib/money';
import { useSession, usePrimaryWallet } from '../lib/session';
import { Badge, Button, Card, EmptyState, ErrorList, Field, Skeleton } from '../components/ui';
import styles from './Money.module.css';

const KIND_LABEL: Readonly<Record<string, string>> = {
  BANK: 'Ngân hàng',
  EWALLET: 'Ví điện tử',
  CRYPTO: 'Tiền mã hóa',
};

const ACCOUNT_STATUS_LABEL: Readonly<Record<string, string>> = {
  PENDING: 'Chờ đối chiếu',
  ACTIVE: 'Đã đối chiếu',
  BLOCKED: 'Bị chặn',
};

/** Huong dan khac phuc cho tung ly do chan rut. */
const REMEDY: Readonly<Record<string, { text: string; to?: string; label?: string }>> = {
  WITHDRAWAL_KYC_REQUIRED: {
    text: 'Cần hoàn tất định danh trước khi rút lần đầu.',
    to: '/tai-khoan/dinh-danh',
    label: 'Nộp hồ sơ',
  },
  WITHDRAWAL_WAGERING_INCOMPLETE: {
    text: 'Còn thưởng chưa hoàn thành điều kiện vòng cược. Có thể hủy thưởng để rút phần tiền thật.',
    to: '/vi',
    label: 'Xem thưởng',
  },
  WITHDRAWAL_COOLDOWN_ACTIVE: {
    text: 'Vừa đổi thông tin nhạy cảm nên tạm thời chưa rút được. Thử lại sau thời gian chờ.',
  },
  // May chu dung MOT ma nay cho ba tinh huong khac nhau (chua duyet, moi them,
  // dang bi tai khoan khac dung). Van ban chung o day noi dung cho ca ba; truong
  // hop moi them co van ban rieng vi chi no moi tu het han theo thoi gian.
  WITHDRAWAL_PAYOUT_ACCOUNT_INVALID: {
    text: 'Tài khoản nhận này chưa dùng để rút được. Chọn tài khoản khác hoặc thêm tài khoản mới.',
  },
  WITHDRAWAL_AMOUNT_OUT_OF_RANGE: {
    text: 'Số tiền rút nằm ngoài mức cho phép của kênh chi trả.',
  },
  WITHDRAWAL_LIMIT_EXCEEDED: {
    text: 'Vượt hạn mức rút của kỳ hiện tại.',
  },
  WITHDRAWAL_TURNOVER_REQUIRED: {
    text: 'Chưa đạt doanh số cược tối thiểu trên số tiền đã nạp.',
  },
  WITHDRAWAL_NAME_MISMATCH: {
    text: 'Tên chủ tài khoản nhận phải trùng tên trên giấy tờ định danh đã duyệt.',
  },
  WITHDRAWAL_TOO_MANY_PENDING: {
    text: 'Đang có quá nhiều lệnh rút chờ xử lý. Chờ xử lý xong rồi tạo lệnh mới.',
  },
  WITHDRAWAL_DISPUTE_OPEN: {
    text: 'Đang có tranh chấp thanh toán chưa xử lý xong.',
  },
  WALLET_INSUFFICIENT_FUNDS: {
    text: 'Số dư khả dụng không đủ. Phần thưởng và phần đang bị khóa không dùng để rút được.',
  },
};

export function WithdrawPage(): JSX.Element {
  const { isAuthenticated, me, refresh } = useSession();
  const wallet = usePrimaryWallet();
  const queryClient = useQueryClient();

  const [amount, setAmount] = useState('');
  const [accountId, setAccountId] = useState<string | null>(null);
  const [acceptFee, setAcceptFee] = useState(false);
  const [created, setCreated] = useState<WithdrawalCreated | null>(null);
  const [adding, setAdding] = useState(false);

  const currency = me?.currency ?? 'USD';

  const accounts = useQuery({
    queryKey: ['payout-accounts'],
    queryFn: api.payoutAccounts.list,
    enabled: isAuthenticated,
  });

  const amountMinor = useMemo(() => {
    try {
      return amount ? parseAmount(amount, currency) : 0n;
    } catch {
      return 0n;
    }
  }, [amount, currency]);

  /**
   * Chi co MOT tai khoan nhan dung duoc thi chon san.
   * Bat nguoi choi bam chon trong danh sach mot phan tu la buoc thua, va neu ho
   * khong bam thi nut gui bi khoa ma khong ro vi sao.
   */
  const usableAccounts = useMemo(
    () =>
      (accounts.data?.accounts ?? []).filter(
        (a) => a.status !== 'BLOCKED' && new Date(a.usable_from).getTime() <= Date.now(),
      ),
    [accounts.data],
  );

  useEffect(() => {
    if (accountId === null && usableAccounts.length === 1) {
      setAccountId(usableAccounts[0]!.id);
    }
  }, [accountId, usableAccounts]);

  // Goi lien tuc, ke ca khi chua nhap so tien: nguoi choi thay ngay minh
  // dang vuong gi ma khong phai thu truoc.
  const eligibility = useQuery({
    queryKey: ['withdrawal-eligibility', amountMinor.toString(), accountId],
    queryFn: () =>
      api.withdrawal.eligibility(
        amountMinor > 0n ? amountMinor.toString() : undefined,
        accountId ?? undefined,
      ),
    enabled: isAuthenticated,
    retry: false,
    staleTime: 3_000,
  });

  const create = useMutation({
    mutationFn: () =>
      api.withdrawal.create(
        {
          payout_account_id: accountId!,
          amount_minor: amountMinor.toString(),
          accept_turnover_fee: acceptFee,
        },
        newIdempotencyKey(),
      ),
    onSuccess: (result) => {
      setCreated(result);
      setAmount('');
      refresh();
      void queryClient.invalidateQueries({ queryKey: ['withdrawal-eligibility'] });
    },
  });

  const forfeit = useMutation({
    mutationFn: api.bonuses.forfeit,
    onSuccess: () => {
      refresh();
      void queryClient.invalidateQueries();
    },
  });

  if (!isAuthenticated) return <EmptyState title="Đăng nhập để rút tiền" />;

  if (created) {
    return (
      <div className={styles.page}>
        <h1 className={styles.title}>Đã tạo lệnh rút</h1>
        <Card>
          <div className={styles.instruction}>
            <Row label="Số tiền rút" value={`${formatMoney(created.amount)} ${currency}`} />
            {BigInt(created.fee.amount_minor) > 0n && (
              <Row label="Phí xử lý" value={`${formatMoney(created.fee)} ${currency}`} tone="warn" />
            )}
            <Row
              label="Thực nhận"
              value={`${formatMoney(created.net_amount)} ${currency}`}
              strong
            />
            <p className={styles.note}>
              {created.status === 'PENDING_REVIEW'
                ? 'Lệnh đang chờ kiểm duyệt. Tiền đã được giữ lại từ số dư khả dụng.'
                : 'Lệnh đã được duyệt và đang chờ gửi đi chi trả.'}
            </p>
            <div className={styles.instructionActions}>
              <Link to="/vi"><Button variant="secondary">Xem ví</Button></Link>
              <Button variant="ghost" onClick={() => setCreated(null)}>Tạo lệnh khác</Button>
            </div>
          </div>
        </Card>
      </div>
    );
  }

  const blockers = eligibility.data?.blockers ?? [];
  const hasWageringBlock = blockers.some((b) => b.code === 'WITHDRAWAL_WAGERING_INCOMPLETE');
  const needsFee = eligibility.data?.requires_turnover_fee ?? false;
  const error = create.error instanceof ApiError ? create.error : null;
  const canSubmit =
    accountId !== null &&
    amountMinor > 0n &&
    blockers.length === 0 &&
    (!needsFee || acceptFee);

  /**
   * Nut bi khoa thi phai noi ro con thieu gi.
   * Nut xam ma khong giai thich la nguoi choi bam vai lan roi bo di.
   */
  const missing =
    accountId === null
      ? usableAccounts.length > 0
        ? 'Chọn tài khoản nhận để tiếp tục.'
        : 'Thêm một tài khoản nhận để tiếp tục.'
      : amountMinor <= 0n
        ? 'Nhập số tiền cần rút.'
        : needsFee && !acceptFee
          ? 'Cần xác nhận đồng ý chịu phí xử lý.'
          : null;

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>Rút tiền</h1>

      {wallet && (
        <div className={styles.limitBar}>
          <span>Rút được tối đa</span>
          <span className={`numeric ${styles.limitValue}`}>
            {eligibility.data
              ? `${formatMoney(eligibility.data.max_withdrawable)} ${currency}`
              : `${formatMoney(wallet.available)} ${currency}`}
          </span>
        </div>
      )}

      {/* Hien MOI vuong mac ngay tu dau, kem huong dan khac phuc tung loai */}
      {blockers.length > 0 && (
        <Card title="Chưa rút được">
          <ul className={styles.blockers}>
            {blockers.map((b, i) => (
              <BlockerRow key={i} blocker={b} />
            ))}
          </ul>

          {hasWageringBlock && (
            <div className={styles.forfeit}>
              <p className={styles.forfeitText}>
                Hủy thưởng sẽ <strong>thu hồi toàn bộ số dư thưởng</strong>, tiền thật giữ nguyên.
                Thao tác này không hoàn tác được.
              </p>
              <Button
                size="sm"
                variant="danger"
                loading={forfeit.isPending}
                onClick={() => forfeit.mutate()}
              >
                Hủy thưởng để rút
              </Button>
            </div>
          )}
        </Card>
      )}

      <Card
        title="Tài khoản nhận"
        action={
          !adding && (
            <Button size="sm" variant="ghost" onClick={() => setAdding(true)}>
              Thêm
            </Button>
          )
        }
      >
        {adding ? (
          <AddAccountForm
            onDone={() => {
              setAdding(false);
              void queryClient.invalidateQueries({ queryKey: ['payout-accounts'] });
            }}
            onCancel={() => setAdding(false)}
          />
        ) : accounts.isLoading ? (
          <Skeleton height={60} />
        ) : accounts.data && accounts.data.accounts.length > 0 ? (
          <ul className={styles.accounts}>
            {accounts.data.accounts.map((a) => (
              <AccountRow
                key={a.id}
                account={a}
                selected={accountId === a.id}
                onSelect={() => setAccountId(a.id)}
              />
            ))}
          </ul>
        ) : (
          <p className={styles.muted}>Chưa có tài khoản nhận nào. Thêm một tài khoản để rút tiền.</p>
        )}
      </Card>

      <Card title="Số tiền">
        <div className={styles.amountBlock}>
          <div className={styles.amountRow}>
            <input
              className={`numeric ${styles.amountInput}`}
              inputMode="decimal"
              placeholder="0"
              value={amount}
              onChange={(e) => setAmount(e.target.value)}
              aria-label="Số tiền rút"
            />
            <span className={styles.amountCurrency}>{currency}</span>
          </div>

          {eligibility.data && (
            <button
              className={styles.maxBtn}
              onClick={() =>
                setAmount(formatMoney(eligibility.data!.max_withdrawable).replace(/,/g, ''))
              }
            >
              Rút tối đa
            </button>
          )}

          {/* WDR-R11: chua dat doanh so cuoc toi thieu thi bi tru phi.
              Phai hoi y kien ro rang, khong am tham tru. */}
          {needsFee && (
            <label className={styles.feeConsent}>
              <input
                type="checkbox"
                checked={acceptFee}
                onChange={(e) => setAcceptFee(e.target.checked)}
              />
              <span>
                Chưa đạt doanh số cược tối thiểu trên số tiền đã nạp. Rút bây giờ sẽ bị{' '}
                <strong>trừ phí xử lý</strong> và lệnh sẽ chuyển sang kiểm duyệt thủ công.
              </span>
            </label>
          )}

          {error && <ErrorList messages={error.messages} />}

          <Button
            size="lg"
            fullWidth
            loading={create.isPending}
            disabled={!canSubmit}
            onClick={() => create.mutate()}
          >
            Tạo lệnh rút
          </Button>

          {missing && blockers.length === 0 && <p className={styles.warn}>{missing}</p>}

          <p className={styles.note}>
            Tiền được giữ lại khỏi số dư khả dụng ngay khi tạo lệnh. Hủy lệnh thì tiền quay lại ví.
          </p>
        </div>
      </Card>
    </div>
  );
}

// ---------------------------------------------------------------------------

function BlockerRow({ blocker }: { blocker: RejectionReason }): JSX.Element {
  const remedy = REMEDY[blocker.code];

  /**
   * Tai khoan moi them: noi ro MOC THOI GIAN dung duoc thay vi noi chung chung.
   * Vuong mac tu het theo thoi gian thi nguoi choi can biet la bao gio, khong
   * phai di lam gi khac.
   */
  const usableFrom = blocker.details?.['usable_from'];
  const text =
    typeof usableFrom === 'string'
      ? `Tài khoản nhận mới thêm, dùng được từ ${new Date(usableFrom).toLocaleString('vi-VN', {
          hour: '2-digit',
          minute: '2-digit',
          day: '2-digit',
          month: '2-digit',
        })}.`
      : (remedy?.text ?? blocker.message);

  return (
    <li className={styles.blocker}>
      <span className={styles.blockerText}>{text}</span>
      {remedy?.to && (
        <Link to={remedy.to} className={styles.link}>{remedy.label ?? 'Xem'}</Link>
      )}
    </li>
  );
}

function AccountRow({
  account,
  selected,
  onSelect,
}: {
  account: PayoutAccount;
  selected: boolean;
  onSelect: () => void;
}): JSX.Element {
  const usableFrom = new Date(account.usable_from);
  const locked = usableFrom.getTime() > Date.now();

  return (
    <li>
      <button
        className={[styles.account, selected ? styles.accountActive : ''].filter(Boolean).join(' ')}
        onClick={onSelect}
        disabled={locked}
        aria-pressed={selected}
      >
        <span className={styles.accountName}>{account.account_name}</span>
        <span className={styles.accountMeta}>
          {KIND_LABEL[account.kind] ?? account.kind}
          {account.bank_code ? ` · ${account.bank_code}` : ''}
        </span>
        {/* WDR-R31: tai khoan moi them bi chan 24 gio — noi ro thay vi chi lam mo di */}
        {locked ? (
          <Badge tone="warning">
            Dùng được từ {usableFrom.toLocaleString('vi-VN', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}
          </Badge>
        ) : (
          <Badge tone={account.status === 'ACTIVE' ? 'positive' : 'neutral'}>
            {ACCOUNT_STATUS_LABEL[account.status] ?? account.status}
          </Badge>
        )}
      </button>
    </li>
  );
}

function AddAccountForm({
  onDone,
  onCancel,
}: {
  onDone: () => void;
  onCancel: () => void;
}): JSX.Element {
  const [kind, setKind] = useState<'BANK' | 'EWALLET' | 'CRYPTO'>('BANK');
  const [accountNumber, setAccountNumber] = useState('');
  const [accountName, setAccountName] = useState('');
  const [bankCode, setBankCode] = useState('');

  const add = useMutation({
    mutationFn: () =>
      api.payoutAccounts.add({
        kind,
        account_number: accountNumber,
        account_name: accountName,
        ...(bankCode ? { bank_code: bankCode } : {}),
      }),
    onSuccess: onDone,
  });

  const error = add.error instanceof ApiError ? add.error : null;

  return (
    <div className={styles.addForm}>
      <div className={styles.kindTabs}>
        {(['BANK', 'EWALLET', 'CRYPTO'] as const).map((k) => (
          <button
            key={k}
            className={[styles.kindTab, kind === k ? styles.kindTabActive : ''].filter(Boolean).join(' ')}
            onClick={() => setKind(k)}
          >
            {k === 'BANK' ? 'Ngân hàng' : k === 'EWALLET' ? 'Ví điện tử' : 'Tiền mã hóa'}
          </button>
        ))}
      </div>

      <Field
        label="Tên chủ tài khoản"
        value={accountName}
        onChange={(e) => setAccountName(e.target.value)}
        hint="Phải trùng tên trên giấy tờ định danh đã duyệt"
        required
      />
      <Field
        label={kind === 'CRYPTO' ? 'Địa chỉ nhận' : 'Số tài khoản'}
        value={accountNumber}
        onChange={(e) => setAccountNumber(e.target.value)}
        required
      />
      {kind === 'BANK' && (
        <Field
          label="Mã ngân hàng"
          value={bankCode}
          onChange={(e) => setBankCode(e.target.value)}
          hint="Không bắt buộc"
        />
      )}

      {error && <ErrorList messages={error.messages} />}

      <p className={styles.note}>
        Tài khoản mới thêm chỉ dùng được sau 24 giờ. Đây là biện pháp chống rút tiền từ tài khoản
        bị chiếm đoạt.
      </p>

      <div className={styles.formActions}>
        <Button
          loading={add.isPending}
          disabled={!accountName || !accountNumber}
          onClick={() => add.mutate()}
        >
          Thêm tài khoản
        </Button>
        <Button variant="ghost" onClick={onCancel}>Hủy</Button>
      </div>
    </div>
  );
}

function Row({
  label,
  value,
  strong,
  tone,
}: {
  label: string;
  value: string;
  strong?: boolean;
  tone?: 'warn';
}): JSX.Element {
  return (
    <div className={styles.summaryRow}>
      <span className={styles.muted}>{label}</span>
      <span
        className={[
          'numeric',
          strong ? styles.summaryStrong : '',
          tone === 'warn' ? styles.summaryWarn : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {value}
      </span>
    </div>
  );
}
